import { WarehouseItemWorkRequestKind } from "@redotech/cockroachdb-codegen/tables/fulfillment/warehouse-item-work-request-kind";
import {
  MAX_MAX_QUESTIONS,
  MIN_MAX_QUESTIONS,
} from "@redotech/redo-agents-cards/returns-agents/util/return-reason-conversation-util";
import { ReturnProcessingLevel } from "@redotech/redo-return-settings-primitives-common/return-processing-level";
import { PillTheme } from "@redotech/redo-storefront-common/pill-theme";
import { ReturnOption, Step, UncoveredPayer } from "./return-flow";
import { FieldId, InputPartType } from "./return-flow-answers";
import {
  FlowType,
  InputContentType,
  InputValidation,
} from "./return-flow-enums";
import type { AiVerificationType } from "./return-flow/ai-verification-registry";
import {
  isDecisionLabelColor,
  type DecisionLabelColor,
} from "./return-flow/decision-label-color";
import {
  ProcessingAction,
  ProcessingActionType,
} from "./return-flow/processing-action";
import { ReturnOptionMethod } from "./return-flow/return-option";
import { StoreCreditType } from "./return-flow/return-option-enums";
import { TriggerType } from "./return-flow/trigger-enums";
import type { ReturnRateSourceSelection } from "./shipping/return-rate-source-selection";

// ── Constants ────────────────────────────────────────────────────────────────

export const SPECIAL_LOCATION_ID = {
  ORIGINAL: "original",
  SHIPMENT_ORIGIN: "shipment-origin",
  REDO_MANAGED: "redo-managed",
} as const;

export type SpecialLocationId =
  (typeof SPECIAL_LOCATION_ID)[keyof typeof SPECIAL_LOCATION_ID];

// ── Helpers ──────────────────────────────────────────────────────────────────

/** The string node ID used by the React Flow editor. */
export type StepRef = string;

export function isFinalizeFlowType(flowType: FlowType | undefined): boolean {
  return (
    flowType === FlowType.FINALIZE_RETURN ||
    flowType === FlowType.FINALIZE_CLAIM ||
    flowType === FlowType.FINALIZE_WARRANTY
  );
}

// ── State interfaces ─────────────────────────────────────────────────────────
// Defined here so that both the frontend valid() delegates and the model-level
// validateModelStep() share the same concrete types. UI step files import
// these instead of defining them locally.

export interface TreatmentState {
  name: string;
  percentage: number;
  next: StepRef | undefined;
}

export interface AbTestState {
  name: string;
  treatments: TreatmentState[];
  customTitle?: string;
}

export interface AddReturnTagState {
  tags: { name: string; color: string }[];
  next: StepRef | undefined;
  customTitle?: string;
}

export interface AddExchangeOrderTagsState {
  tags: string[];
  copyOriginalOrderTags?: boolean;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface CustomGuidanceState {
  id: string;
  description: string;
  next: StepRef | undefined;
}

export interface AiReturnReasonsState {
  next: StepRef | undefined;
  nextWrongProduct: StepRef | undefined;
  nextDamaged: StepRef | undefined;
  /**
   * @deprecated Use a `customGuidance` entry with id `"exchange"` instead.
   * Kept on state for legacy reads; auto-promoted into customGuidance by
   * `aiReturnReasonsFromModel`. New saves never set this.
   */
  nextExchange: StepRef | undefined;
  customGuidance?: CustomGuidanceState[];
  additionalAiInfo?: string;
  maxQuestions?: number;
  merchantGuidance?: string;
  customQuestion?: string;
  customExplanationSubtext?: string;
}

export interface BlockState {
  message: string;
  customTitle?: string;
}

export interface DiscountCodeNameState {
  prefix: string;
  suffix: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface ExchangeShippingNameState {
  shippingName: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface FinalSaleReturnState {
  next?: StepRef;
  customTitle?: string;
}

export interface FlagState {
  reason: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface InformationState {
  message: string;
  header: string;
  continueButtonText: string;
  next?: StepRef;
  customTitle?: string;
}

export interface InputState {
  partUUID?: string;
  next: StepRef | undefined;
  prompt: string;
  description: string;
  type: InputPartType | undefined;
  fieldId?: FieldId;
  validation: InputValidation;
  exampleImage: URL | undefined;
  attempted?: boolean;
  customTitle?: string;
  minLength?: number;
  maxLength?: number;
  contentType?: InputContentType;
  customRegex?: string;
  customRegexErrorMessage?: string;
  useAdvancedValidation?: boolean;
  aiVerification?: AiVerificationType;
  aiVerificationStrictness?: number;
  /** Parts beyond the first, preserved verbatim; the editor edits only parts[0]. */
  extraParts?: Step.InputPart[];
}

export interface ManualReviewState {
  reason: string;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface ChoiceState {
  optionUUID?: string;
  name: string;
  next: StepRef | undefined;
  code?: string;
  additionalAiInfo?: string;
}

/** The only way to author an option: every one is born with its stable id. */
export function newChoiceState(name = ""): ChoiceState {
  return { optionUUID: crypto.randomUUID(), name, next: undefined };
}

export interface MultipleChoiceState {
  stepUUID?: string;
  prompt: string;
  choices: ChoiceState[];
  description: string;
  reason: boolean;
  shuffle?: boolean;
  attempted?: boolean;
  customTitle?: string;
  additionalAiInfo?: string;
}

export interface OverrideAdjustmentState {
  adjustmentFlat: string;
  adjustmentPercent: string;
  /** Per-currency flat amount overrides. Keys are Currency enum values, values are amount strings. */
  currencyOverrides?: Record<string, string>;
  perCurrencyAmountsEnabled?: boolean;
  next: StepRef | undefined;
  customTitle?: string;
  isUseItOrLoseIt?: boolean;
}

export interface OverrideGreenReturnState {
  greenReturnOverride: boolean;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface RejectState {
  message: string;
  customTitle?: string;
}

/**
 * UI-specific representation of a return option. Replaces the model's
 * `method: ReturnOptionMethod` + `adjustment: Adjustment` with string-form
 * fields for editing precision, and adds `type`/`creditType` discriminants.
 */
export type ReturnOptionState = Omit<ReturnOption, "method" | "adjustment"> & {
  type: ReturnOptionMethod["type"] | undefined;
  creditType: StoreCreditType;
  adjustmentFlat: string;
  adjustmentPercent: string;
  adjustmentType?: "flat" | "percent";
  /** Per-currency flat amount overrides. Keys are Currency enum values, values are amount strings. */
  currencyOverrides?: Record<string, string>;
  perCurrencyAmountsEnabled?: boolean;
  isUseItOrLoseIt?: boolean;
  refundPointsRedeemedForItem?: boolean;
  deductEarnedPointsFromRefund?: boolean;
  reimburseForFinalSale?: boolean;
};

export interface ReturnState extends Omit<Step.Return, "type" | "options"> {
  options: ReturnOptionState[];
  exchangeAgentOverrides?: Step.ExchangeAgentOverrides;
}

export interface SendEmailState {
  emailId: string;
  emailName?: string;
  emailType?: string;
  next?: StepRef;
  customTitle?: string;
}

export interface SendSmsState {
  smsId: string;
  smsName?: string;
  smsType?: string;
  next?: StepRef;
  customTitle?: string;
}

export interface SetReturnLocationsState {
  locations: string[];
  restockExternalLocationIds?: Record<string, string | null>;
  customTitle?: string;
  rateSourceSelection?: ReturnRateSourceSelection;
}

export interface DonateState {
  locations: string[];
  restockExternalLocationIds?: Record<string, string | null>;
  customTitle?: string;
}

export interface SubmitClaimOption {
  method: ReturnOptionMethod["type"];
  creditType: StoreCreditType;
  requireSendBack: boolean;
  chargeCustomerForNewOrderShipping?: boolean;
  variantExchangeAddOn?: boolean;
  variantExchangeAddOnVariantId?: string;
  variantExchangeAddOnTitle?: string;
  variantExchangeAddOnAcceptButton?: string;
  variantExchangeAddOnRejectButton?: string;
  carryOverLineItemProperties?: boolean;
}

export interface SubmitClaimState {
  option: SubmitClaimOption;
  customTitle?: string;
}

export interface SubmitWarrantyOption {
  method: ReturnOptionMethod["type"];
  creditType: StoreCreditType;
  requireSendBack: boolean;
  labelPayer: UncoveredPayer;
  adjustmentFlat: string | undefined;
  adjustmentPercent: string | undefined;
  currencyOverrides?: Record<string, string>;
  perCurrencyAmountsEnabled?: boolean;
  manual_label?: boolean;
  discountTitle?: string;
  /** Only meaningful when method is a same-item exchange. */
  chargeCustomerForNewOrderShipping?: boolean;
  carryOverLineItemProperties?: boolean;
}

export interface SubmitWarrantyState {
  option: SubmitWarrantyOption;
  customTitle?: string;
}

export interface CombineLabelState {
  customTitle?: string;
}

export interface DoNothingState {}

export interface DynamicBundleAdjustmentState {
  next: StepRef | undefined;
  customTitle?: string;
}

export interface SeparateLabelsState {
  customTitle?: string;
}

export interface SubmitRepairOption {
  labelPayer: UncoveredPayer;
  chargeFee: boolean;
  repairFee: Step.RepairFeeConfig;
}

export interface SubmitRepairState {
  customTitle?: string;
  option: SubmitRepairOption;
}

export interface SubmitWarrantyRegistrationState {
  customTitle?: string;
}

export interface TriggerState {
  triggerType: TriggerType;
  next: StepRef | undefined;
  customTitle?: string;
}

export interface WaitState {
  numDays: number;
  next: StepRef | undefined;
  customTitle?: string;
}

// ── RMR (returns-processing) flow state interfaces ───────────────────────────
// `nextTrue` / `nextFalse` mirror the existing condition pattern even though
// the persisted RMR model fields are `nextTrueId` / `nextFalseId` — the
// fromModel / toModel converters bridge the names. Editor refs are always
// StepRef (string); the persisted model uses numeric array indices.

export interface SortState {
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  destination: string;
  customTitle?: string;
}

export interface ReturnProcessingInformationState {
  header: string;
  message: string;
  continueButtonText: string;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
  imageUrl?: string;
}

export interface EvidenceState {
  prompt: string;
  notesLabel?: string;
  notesPlaceholder?: string;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
  imageUrl?: string;
}

export interface AddUndeclaredItemState {
  prompt: string;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
}

export interface VerificationState {
  prompt: string;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
}

export interface VerifyItemVariantState {
  prompt: string;
  matchLabel: string;
  mismatchLabel: string;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
}

export interface MakeServiceRequestState {
  requestKind: WarehouseItemWorkRequestKind;
  photoInstructionsId?: string | null;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
}

export interface ReturnProcessingDecisionState {
  prompt: string;
  trueLabel: string;
  falseLabel: string;
  nextTrue?: StepRef;
  nextFalse?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
  decisionKey?: string;
  imageUrl?: string;
  trueLabelColor?: DecisionLabelColor;
  falseLabelColor?: DecisionLabelColor;
}

export interface ProcessingActionState {
  action: ProcessingAction;
  next?: StepRef;
  scope: Step.ReturnProcessingStepScope;
  customTitle?: string;
}

// ── Validation result type ────────────────────────────────────────────────────

export type StepValidationResult =
  | { valid: true }
  | { valid: false; errors: string[] };

// ── Validation functions ─────────────────────────────────────────────────────

export function isAbTestStepValid(state: AbTestState): StepValidationResult {
  const errors: string[] = [];
  if (!state.treatments.length) {
    errors.push("Must have at least one treatment");
  } else {
    const sum = state.treatments.reduce((acc, t) => acc + t.percentage, 0);
    if (sum !== 100) {
      errors.push(`Treatment percentages must sum to 100 (got ${sum})`);
    }
    for (let i = 0; i < state.treatments.length; i++) {
      const t = state.treatments[i]!;
      if (!t.name) {
        errors.push(`treatments[${i}].name cannot be empty`);
      }
      if (t.next === undefined) {
        errors.push(`treatments[${i}].next is required`);
      }
      if (t.percentage < 0 || t.percentage > 100) {
        errors.push(
          `treatments[${i}].percentage must be between 0 and 100 (got ${t.percentage})`,
        );
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isAddReturnTagStepValid(
  state: AddReturnTagState,
  flowType?: FlowType,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.tags.length) {
    errors.push("Must have at least one tag");
  } else {
    for (let i = 0; i < state.tags.length; i++) {
      const tag = state.tags[i]!;
      if (!tag.name) {
        errors.push(`tags[${i}].name cannot be empty`);
      }
      if (!Object.values(PillTheme).includes(tag.color as PillTheme)) {
        errors.push(
          `tags[${i}].color must be a valid PillTheme value (one of: ${Object.values(PillTheme).join(", ")})`,
        );
      }
    }
  }
  if (state.next === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isAddExchangeOrderTagsStepValid(
  state: AddExchangeOrderTagsState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.tags.length && !state.copyOriginalOrderTags) {
    errors.push("Must have at least one tag or copy original order tags");
  } else {
    const seen = new Set<string>();
    for (let i = 0; i < state.tags.length; i++) {
      const tag = state.tags[i]!;
      const trimmed = tag.trim();
      if (!trimmed) {
        errors.push(`tags[${i}] cannot be empty`);
      } else if (seen.has(trimmed)) {
        errors.push(`tags[${i}] is duplicated`);
      } else {
        seen.add(trimmed);
      }
    }
  }
  if (state.next === undefined) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isAiReturnReasonsStepValid(
  state: AiReturnReasonsState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.next) {
    errors.push("next is required");
  }
  if (!state.nextWrongProduct) {
    errors.push("nextWrongProduct is required");
  }
  if (!state.nextDamaged) {
    errors.push("nextDamaged is required");
  }
  if (
    state.maxQuestions !== undefined &&
    (state.maxQuestions < MIN_MAX_QUESTIONS ||
      state.maxQuestions > MAX_MAX_QUESTIONS)
  ) {
    errors.push(
      `maxQuestions must be between ${MIN_MAX_QUESTIONS} and ${MAX_MAX_QUESTIONS} (got ${state.maxQuestions})`,
    );
  }
  const customGuidance = state.customGuidance ?? [];
  const seenIds = new Set<string>();
  for (const [i, element] of customGuidance.entries()) {
    const g = element!;
    if (!g.id) {
      errors.push(`customGuidance[${i}].id is required`);
    } else if (seenIds.has(g.id)) {
      errors.push(`customGuidance[${i}].id "${g.id}" is duplicated`);
    } else {
      seenIds.add(g.id);
    }
    if (!g.description) {
      errors.push(`customGuidance[${i}].description is required`);
    }
    if (!g.next) {
      errors.push(`customGuidance[${i}].next is required`);
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isBlockStepValid(state: BlockState): StepValidationResult {
  const errors: string[] = [];
  if (!state.message) {
    errors.push("message cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isConditionStepValid({
  conditionStep,
  flowType,
  isConditionValid,
}: {
  conditionStep: { nextTrue?: StepRef; nextFalse?: StepRef };
  flowType?: FlowType;
  isConditionValid: () => { valid: true } | { valid: false; errors: string[] };
}): StepValidationResult {
  const errors: string[] = [];
  if (conditionStep.nextTrue === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("nextTrue is required");
  }
  if (conditionStep.nextFalse === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("nextFalse is required");
  }
  const conditionValidationResult = isConditionValid();
  if (!conditionValidationResult.valid) {
    errors.push(...conditionValidationResult.errors);
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isDiscountCodeNameStepValid(
  state: DiscountCodeNameState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prefix && !state.suffix) {
    errors.push("At least one of prefix or suffix must be set");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isExchangeShippingNameStepValid(
  state: ExchangeShippingNameState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.shippingName === undefined) {
    errors.push("shippingName is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isFinalSaleStepValid(
  state: FinalSaleReturnState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isFlagStepValid(
  state: FlagState,
  flowType?: FlowType,
): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isInformationStepValid(
  state: InformationState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.message) {
    errors.push("message cannot be empty");
  }
  if (!state.header) {
    errors.push("header cannot be empty");
  }
  if (!state.continueButtonText) {
    errors.push("continueButtonText cannot be empty");
  }
  if (state.next === undefined) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isInputStepValid(state: InputState): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined) {
    errors.push("next is required");
  }
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  if (state.type === undefined) {
    errors.push("type is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isManualReviewStepValid(
  state: ManualReviewState,
  flowType?: FlowType,
): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined && !isFinalizeFlowType(flowType)) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isMultipleChoiceStepValid(
  state: MultipleChoiceState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  if (!state.choices.length) {
    errors.push("Must have at least one choice");
  } else {
    for (let i = 0; i < state.choices.length; i++) {
      const choice = state.choices[i]!;
      if (!choice.name) {
        errors.push(`choices[${i}].name cannot be empty`);
      }
      if (choice.next === undefined) {
        errors.push(`choices[${i}].next is required`);
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isOverrideAdjustmentStepValid(
  state: OverrideAdjustmentState,
): StepValidationResult {
  const errors: string[] = [];
  if (
    state.adjustmentFlat === undefined &&
    state.adjustmentPercent === undefined
  ) {
    errors.push(
      "At least one of adjustmentFlat or adjustmentPercent must be set",
    );
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isOverrideGreenReturnStepValid(
  state: OverrideGreenReturnState,
): StepValidationResult {
  const errors: string[] = [];
  if (state.greenReturnOverride === undefined) {
    errors.push("greenReturnOverride is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isRejectStepValid(state: RejectState): StepValidationResult {
  const errors: string[] = [];
  if (!state.message) {
    errors.push("message cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isReturnStepValid(state: ReturnState): StepValidationResult {
  const errors: string[] = [];
  if (!state.options.length) {
    errors.push("Must have at least one option");
  } else {
    for (let i = 0; i < state.options.length; i++) {
      const option = state.options[i]!;
      if (isNaN(parseFloat(option.adjustmentFlat))) {
        errors.push(`options[${i}].adjustmentFlat must be a valid number`);
      }
      if (isNaN(parseFloat(option.adjustmentPercent))) {
        errors.push(`options[${i}].adjustmentPercent must be a valid number`);
      }
      if (
        option.type === ReturnOptionMethod.EXCHANGE &&
        option.variantExchangeAddOn
      ) {
        if (!option.variantExchangeAddOnVariantId) {
          errors.push(
            `options[${i}].variantExchangeAddOnVariantId: Required when variantExchangeAddOn is true`,
          );
        }
        if (!option.variantExchangeAddOnTitle) {
          errors.push(
            `options[${i}].variantExchangeAddOnTitle: Required when variantExchangeAddOn is true`,
          );
        }
        if (!option.variantExchangeAddOnAcceptButton) {
          errors.push(
            `options[${i}].variantExchangeAddOnAcceptButton: Required when variantExchangeAddOn is true`,
          );
        }
        if (!option.variantExchangeAddOnRejectButton) {
          errors.push(
            `options[${i}].variantExchangeAddOnRejectButton: Required when variantExchangeAddOn is true`,
          );
        }
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSendEmailStepValid(
  state: SendEmailState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.emailId) {
    errors.push("emailId cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSendSmsStepValid(state: SendSmsState): StepValidationResult {
  const errors: string[] = [];
  if (!state.smsId) {
    errors.push("smsId cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSetReturnLocationsStepValid(
  state: SetReturnLocationsState,
): StepValidationResult {
  const errors: string[] = [];
  addRateSourceSelectionErrors(errors, state.rateSourceSelection);
  const validLocations = state.locations.filter((loc) => loc !== "");
  if (validLocations.length === 0) {
    errors.push("At least one return location must be set");
  } else {
    for (const locationId of validLocations) {
      if (
        locationId === SPECIAL_LOCATION_ID.ORIGINAL ||
        locationId === SPECIAL_LOCATION_ID.REDO_MANAGED
      ) {
        continue;
      }
      const restockId = state.restockExternalLocationIds?.[locationId];
      if (!restockId) {
        errors.push(
          `Location "${locationId}" is missing a restock external location ID`,
        );
      }
    }
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isDonateStepValid(state: DonateState): StepValidationResult {
  const errors: string[] = [];
  const validLocations = state.locations.filter((loc) => loc !== "");
  if (validLocations.length === 0) {
    errors.push("A fallback location must be selected");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSubmitClaimStepValid(
  state: SubmitClaimState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.option?.method) {
    errors.push("option.method is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSubmitWarrantyStepValid(
  state: SubmitWarrantyState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.option?.method) {
    errors.push("option.method is required");
  }
  if (isNaN(parseFloat(state.option.adjustmentFlat ?? "0"))) {
    errors.push("option.adjustmentFlat must be a valid number");
  }
  if (isNaN(parseFloat(state.option.adjustmentPercent ?? "0"))) {
    errors.push("option.adjustmentPercent must be a valid number");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isTriggerStepValid(state: TriggerState): StepValidationResult {
  const errors: string[] = [];
  if (state.next === undefined) {
    errors.push("next is required");
  }
  if (!state.triggerType) {
    errors.push("triggerType is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isWaitStepValid(state: WaitState): StepValidationResult {
  const errors: string[] = [];
  if (!(state.numDays > 0)) {
    errors.push("numDays must be greater than 0");
  }
  if (state.next === undefined) {
    errors.push("next is required");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isCombineLabelStepValid(
  _state: CombineLabelState,
): StepValidationResult {
  return { valid: true };
}

export function isDynamicBundleAdjustmentStepValid(
  _state: DynamicBundleAdjustmentState,
): StepValidationResult {
  return { valid: true };
}

export function isDoNothingStepValid(
  _state: DoNothingState,
): StepValidationResult {
  return { valid: true };
}

export function isSeparateLabelsStepValid(
  _state: SeparateLabelsState,
): StepValidationResult {
  return { valid: true };
}

export function isSubmitRepairStepValid(
  state: SubmitRepairState,
): StepValidationResult {
  const errors: string[] = [];
  const repairFee = state.option.repairFee;
  switch (repairFee.strategy) {
    case Step.RepairFeeMode.Instant:
      if (repairFee.amount < 0 || isNaN(repairFee.amount)) {
        errors.push("Repair fee amount must be zero or greater");
      }
      break;
    case Step.RepairFeeMode.Deferred:
      if (repairFee.minPrice < 0 || isNaN(repairFee.minPrice)) {
        errors.push("Minimum repair fee must be zero or greater");
      }
      if (
        isNaN(repairFee.maxPrice) ||
        repairFee.maxPrice < repairFee.minPrice
      ) {
        errors.push(
          "Maximum repair fee must be greater than or equal to the minimum",
        );
      }
      break;
    case Step.RepairFeeMode.Free:
      break;
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isSubmitWarrantyRegistrationStepValid(
  _state: SubmitWarrantyRegistrationState,
): StepValidationResult {
  return { valid: true };
}

// ── RMR (returns-processing) flow validators ─────────────────────────────────
// Terminal RMR steps don't require `next` — they implicitly end the flow when
// the warehouse worker completes them. Non-terminal steps that depend on a
// follow-up question (e.g. evidence's notes panel) likewise leave next
// optional today; the evaluator just stops if next is undefined.

export function isSortStepValid(state: SortState): StepValidationResult {
  const errors: string[] = [];
  if (!state.destination) {
    errors.push("destination cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export interface RedoManagedLocationState {
  customTitle?: string;
  returnsCenterIds: string[];
  serviceLevel: ReturnProcessingLevel;
  refulfillmentEnabled?: boolean;
  processingFee?: string;
  consolidationScheduleDays?: string;
  rateSourceSelection?: ReturnRateSourceSelection;
}

export function isReturnProcessingInformationStepValid(
  state: ReturnProcessingInformationState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.header) {
    errors.push("header cannot be empty");
  }
  if (!state.message) {
    errors.push("message cannot be empty");
  }
  if (!state.continueButtonText) {
    errors.push("continueButtonText cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isEvidenceStepValid(
  state: EvidenceState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isAddUndeclaredItemStepValid(
  state: AddUndeclaredItemState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isVerificationStepValid(
  state: VerificationState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isVerifyItemVariantStepValid(
  state: VerifyItemVariantState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  if (!state.matchLabel) {
    errors.push("matchLabel cannot be empty");
  }
  if (!state.mismatchLabel) {
    errors.push("mismatchLabel cannot be empty");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

/**
 * A photo request the warehouse cannot act on is worse than no step at all:
 * without directions the operator is shown an empty walkthrough, so authoring
 * is blocked until a guidance set is picked.
 */
export function isMakeServiceRequestStepValid(
  state: MakeServiceRequestState,
): StepValidationResult {
  const errors: string[] = [];
  if (
    state.requestKind === WarehouseItemWorkRequestKind.Photo &&
    !state.photoInstructionsId
  ) {
    errors.push("photoInstructionsId is required for photo requests");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isReturnProcessingDecisionStepValid(
  state: ReturnProcessingDecisionState,
): StepValidationResult {
  const errors: string[] = [];
  if (!state.prompt) {
    errors.push("prompt cannot be empty");
  }
  if (!state.trueLabel) {
    errors.push("trueLabel cannot be empty");
  }
  if (!state.falseLabel) {
    errors.push("falseLabel cannot be empty");
  }
  if (
    state.trueLabelColor !== undefined &&
    !isDecisionLabelColor(state.trueLabelColor)
  ) {
    errors.push("trueLabelColor must be a valid DecisionLabelColor");
  }
  if (
    state.falseLabelColor !== undefined &&
    !isDecisionLabelColor(state.falseLabelColor)
  ) {
    errors.push("falseLabelColor must be a valid DecisionLabelColor");
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

export function isProcessingActionStepValid(
  state: ProcessingActionState,
): StepValidationResult {
  if (
    state.action.type === ProcessingActionType.AssignGrade &&
    !state.action.grade
  ) {
    return { valid: false, errors: ["grade is required"] };
  }
  return { valid: true };
}

export function isRedoManagedLocationStepValid(
  state: RedoManagedLocationState,
): StepValidationResult {
  const errors: string[] = [];
  addRateSourceSelectionErrors(errors, state.rateSourceSelection);
  if (!state.returnsCenterIds || state.returnsCenterIds.length === 0) {
    errors.push("At least one returns center is required");
  }
  if (!state.serviceLevel) {
    errors.push("Service level is required");
  }
  if (
    state.refulfillmentEnabled &&
    state.serviceLevel === ReturnProcessingLevel.VERIFICATION
  ) {
    errors.push(
      "Refulfillment cannot be enabled with VERIFICATION service level",
    );
  }
  return errors.length ? { valid: false, errors } : { valid: true };
}

function addRateSourceSelectionErrors(
  errors: string[],
  rateSourceSelection?: ReturnRateSourceSelection,
): void {
  if (rateSourceSelection && rateSourceSelection.sources.length === 0) {
    errors.push("At least one carrier account or dropoff must be selected");
  }
}
